import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck, FileText, MonitorSmartphone, Banknote, Phone, MapPin,
  ArrowRight, BadgeCheck, TrendingUp, HandCoins, Users, Landmark,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Calculator } from "@/components/Calculator";
import { Logo, LogoMark } from "@/components/Logo";
import { WhatsappButton, WhatsAppIcon, waLink } from "@/components/WhatsappButton";
import { useLang } from "@/i18n";
import { PLANS_META, DSA_SLABS, IMAGES, PLAN_IMAGES, QR_PAYMENT, WHATSAPP_NUMBER, LOAN_PLAN_TABLE } from "@/constants";
import { api, API, inr } from "@/api";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="h-px w-10 bg-[#D4AF37]" />
    <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#B5952F]">{children}</span>
  </div>
);

const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const lines = [t("hero_l1"), t("hero_l2"), t("hero_l3")];

  return (
    <section ref={ref} className="relative pt-32 lg:pt-40 pb-20 overflow-hidden" data-testid="hero-section">
      <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-[#F7F0D8] blur-3xl opacity-70 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-[#D4AF37] px-4 py-2 mb-10"
            data-testid="hero-eyebrow"
          >
            <LogoMark size={18} />
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-[#1A1A1A]">{t("hero_eyebrow")}</span>
          </motion.div>
          <h1 key={lang} className="font-display font-bold tracking-tight text-[#1A1A1A] text-4xl sm:text-5xl lg:text-6xl leading-[1.08]" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-[#B5952F]" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 text-base md:text-lg text-[#5C5C5C] leading-relaxed max-w-xl"
          >
            {t("hero_sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#plans"
              data-testid="hero-explore-plans-button"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-8 py-4 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300"
            >
              {t("hero_cta1")} <ArrowRight size={16} />
            </a>
            <a
              href="#calculator"
              data-testid="hero-calculator-button"
              className="inline-flex items-center gap-2 border-2 border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {t("hero_cta2")}
            </a>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative"
          >
            <div className="absolute -inset-4 border border-[#D4AF37] translate-x-6 translate-y-6 pointer-events-none" />
            <div className="overflow-hidden">
              <motion.img
                src={IMAGES.hero}
                alt="Matrix Works — modern architecture"
                style={{ y: imgY }}
                className="w-full h-[420px] lg:h-[520px] object-cover scale-110"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-8 -left-4 sm:-left-8 bg-[#1A1A1A] text-white p-6 noise-overlay" data-testid="hero-stat-card">
              <div className="font-display text-3xl font-bold text-[#D4AF37] tabular">₹5 Cr+</div>
              <div className="text-xs uppercase tracking-[0.2em] mt-1 text-white/70">{t("stat_funds")}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Stats = () => {
  const { t } = useLang();
  const stats = [
    { value: "4+", label: t("stat_years") },
    { value: "350+", label: t("stat_clients") },
    { value: "₹5 Cr+", label: t("stat_funds") },
    { value: "₹15 L+", label: t("stat_turnover") },
  ];
  return (
    <section className="border-y border-[#E5E2D9] bg-white" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className={`py-10 lg:py-14 px-4 text-center ${i > 0 ? "border-l border-[#E5E2D9]" : ""}`}>
            <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tabular">{s.value}</div>
            <div className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F]">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Marquee = () => {
  const { t } = useLang();
  const items = [t("marquee_1"), t("marquee_2"), t("marquee_3"), t("marquee_4"), t("marquee_5")];
  const row = [...items, ...items, ...items];
  return (
    <div className="bg-[#1A1A1A] py-5 overflow-hidden noise-overlay" data-testid="marquee-strip">
      <div className="flex w-max animate-marquee gap-0">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center gap-6 px-6 text-sm font-semibold tracking-[0.15em] uppercase text-white/80 whitespace-nowrap">
                {item} <LogoMark size={14} />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const OffersTicker = () => {
  const { lang } = useLang();
  const [offers, setOffers] = useState([]);
  useEffect(() => {
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
  }, []);
  if (!offers.length) return null;
  const items = offers.map((o) => (lang === "gu" && o.text_gu ? o.text_gu : o.text_en));
  const row = [...items, ...items, ...items, ...items];
  return (
    <div className="bg-[#D4AF37] py-3.5 overflow-hidden" data-testid="offers-ticker">
      <div className="flex w-max animate-marquee gap-0">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center gap-4 px-8 text-sm font-bold uppercase tracking-[0.15em] text-[#1A1A1A] whitespace-nowrap">
                <span className="w-1.5 h-1.5 bg-[#1A1A1A] rotate-45 inline-block shrink-0" /> {item}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const planIcons = [HandCoins, TrendingUp, BadgeCheck, FileText, null, Landmark];

const Plans = () => {
  const { t } = useLang();
  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];
  return (
    <section id="plans" className="py-24 lg:py-32" data-testid="plans-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{t("plans_eyebrow")}</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] max-w-xl">{t("plans_title")}</h2>
            <div>
              <p className="text-base md:text-lg text-[#5C5C5C] max-w-md leading-relaxed">{t("plans_sub")}</p>
              <WhatsappButton
                label={t("wa_invest")}
                message="Hello Matrix Works, I want to invest in a plan. Please guide me."
                testid="plans-whatsapp-button"
                className="mt-6"
              />
            </div>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-6">
          {PLANS_META.map((p, i) => {
            const Icon = planIcons[i];
            return (
              <Reveal key={p.id} delay={i * 0.08} className={spans[i]}>
                <div
                  className={`group relative h-full border border-[#E5E2D9] bg-white p-8 lg:p-10 flex flex-col justify-between min-h-[280px] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-[#D4AF37] ${
                    p.id === "gold_recurring" ? "noise-overlay" : ""
                  }`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {p.id === "gold_recurring" && (
                    <img src={IMAGES.gold} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.14] pointer-events-none" />
                  )}
                  <div className="relative">
                    <div className="flex items-start justify-between">
                      <span className="inline-block bg-[#F7F0D8] text-[#8a6d1c] text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5">
                        {t(p.tagKey)}
                      </span>
                      {Icon && <Icon size={28} strokeWidth={1.5} className="text-[#D4AF37]" />}
                    </div>
                    <h3 className="font-display text-2xl lg:text-3xl font-bold text-[#1A1A1A] mt-6">{t(p.nameKey)}</h3>
                    <p className="text-sm text-[#5C5C5C] leading-relaxed mt-3">{t(p.descKey)}</p>
                  </div>
                  <Link
                    to="/register"
                    data-testid={`plan-cta-${p.id}`}
                    className="relative mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A] group-hover:text-[#B5952F] transition-colors duration-200 w-fit"
                  >
                    {t("plan_cta")}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-20">
          <Reveal>
            <Eyebrow>{t("gallery_eyebrow")}</Eyebrow>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
              <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] max-w-xl">{t("gallery_title")}</h3>
              <p className="text-sm md:text-base text-[#5C5C5C] max-w-md leading-relaxed">{t("gallery_sub")}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {["daily_recurring", "daily_roi", "monthly_roi"].map((id, i) => {
              const meta = PLANS_META.find((p) => p.id === id);
              return (
                <Reveal key={id} delay={i * 0.1}>
                  <a
                    href={PLAN_IMAGES[id]}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`plan-poster-${id}`}
                    className="group block bg-[#0A0A0A] border border-[#E5E2D9] hover:border-[#D4AF37] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                  >
                    <div className="h-[380px] flex items-center justify-center overflow-hidden p-2">
                      <img
                        src={PLAN_IMAGES[id]}
                        alt={t(meta.nameKey)}
                        className="max-h-full w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="border-t border-[#D4AF37]/30 px-5 py-4 flex items-center justify-between gap-3">
                      <span className="font-display font-bold text-white text-sm sm:text-base">{t(meta.nameKey)}</span>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] font-bold whitespace-nowrap">{t(meta.tagKey)}</span>
                    </div>
                  </a>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const CalcSection = () => {
  const { t } = useLang();
  return (
    <section id="calculator" className="py-24 lg:py-32 bg-[#F3F1EB]" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-16">
          <Eyebrow>{t("calc_eyebrow")}</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] max-w-2xl">{t("calc_title")}</h2>
          <p className="text-base md:text-lg text-[#5C5C5C] mt-4 max-w-xl leading-relaxed">{t("calc_sub")}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <Calculator />
        </Reveal>
      </div>
    </section>
  );
};

const LoanPlanSection = () => {
  const { t } = useLang();
  const highlights = [t("loan_hl_days"), t("loan_hl_loan"), t("loan_hl_emi"), t("loan_hl_tenure")];
  const cols = ["loan_col_deposit", "loan_col_daily", "loan_col_loan", "loan_col_tenure", "loan_col_emi", "loan_col_profit", "loan_col_repay", "loan_col_left"];
  return (
    <section id="loan-plan" className="bg-[#1A1A1A] py-24 lg:py-32 noise-overlay" data-testid="loan-plan-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-2xl">{t("loan_sec_title")}</h2>
            <p className="text-base md:text-lg text-white/60 max-w-md leading-relaxed">{t("loan_sec_sub")}</p>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="grid lg:grid-cols-12 gap-10 items-start mb-14">
            <div className="lg:col-span-5">
              <img src={PLAN_IMAGES.loan_plan} alt={t("p6_name")} className="w-full border border-[#3A3A3A]" data-testid="loan-plan-poster" />
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#3A3A3A] border border-[#3A3A3A]">
                {highlights.map((h) => (
                  <div key={h} className="bg-[#232323] p-6 flex items-center gap-3">
                    <span className="w-2 h-2 bg-[#D4AF37] rotate-45 shrink-0" />
                    <span className="text-sm font-bold text-white">{h}</span>
                  </div>
                ))}
              </div>
              <WhatsappButton
                label={t("wa_invest")}
                message="Hello Matrix Works, I want to invest in the Investment with Loan Plan. Please guide me."
                testid="loan-plan-whatsapp-button"
                className="mt-8"
              />
              <p className="text-xs text-white/40 mt-6 leading-relaxed">{t("loan_note")}</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="overflow-x-auto border border-[#3A3A3A]" data-testid="loan-plan-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-[#D4AF37] text-[#1A1A1A]">
                  {cols.map((c) => (
                    <th key={c} className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap">{t(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LOAN_PLAN_TABLE.map((r) => (
                  <tr key={r.deposit} className="border-t border-[#2A2A2A] hover:bg-[#232323] transition-colors duration-150" data-testid={`loan-row-${r.deposit}`}>
                    <td className="px-4 py-3 tabular font-bold text-white whitespace-nowrap">{inr(r.deposit)}</td>
                    <td className="px-4 py-3 tabular text-[#D4AF37] font-bold whitespace-nowrap">{inr(r.daily)}</td>
                    <td className="px-4 py-3 tabular text-white whitespace-nowrap">{inr(r.loan)}</td>
                    <td className="px-4 py-3 tabular text-white/70 whitespace-nowrap">{r.tenure} {t("loan_months")}</td>
                    <td className="px-4 py-3 tabular text-white/70 whitespace-nowrap">{inr(r.emi)}</td>
                    <td className="px-4 py-3 tabular text-[#4ADE80] font-bold whitespace-nowrap">{inr(r.profit)}</td>
                    <td className="px-4 py-3 tabular text-white/70 whitespace-nowrap">{inr(r.repay)}</td>
                    <td className="px-4 py-3 tabular text-white/70 whitespace-nowrap">{inr(r.left)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const DsaSection = () => {
  const { t } = useLang();
  const features = [
    { icon: BadgeCheck, label: t("dsa_f1") },
    { icon: TrendingUp, label: t("dsa_f2") },
    { icon: HandCoins, label: t("dsa_f3") },
    { icon: Users, label: t("dsa_f4") },
  ];
  return (
    <section id="dsa" className="py-24 lg:py-32 bg-[#1A1A1A] text-white noise-overlay" data-testid="dsa-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{t("dsa_eyebrow")}</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl">{t("dsa_title")}</h2>
            <p className="text-base md:text-lg text-white/60 max-w-md leading-relaxed">{t("dsa_sub")}</p>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="overflow-x-auto border border-white/15">
              <table className="w-full text-sm" data-testid="dsa-slab-table">
                <thead>
                  <tr className="bg-white/5 text-[#D4AF37] text-xs uppercase tracking-[0.15em]">
                    <th className="text-left px-5 py-4 font-bold">{t("slab_business")} (₹)</th>
                    <th className="text-right px-5 py-4 font-bold">{t("slab_payout")}</th>
                    <th className="text-right px-5 py-4 font-bold">{t("slab_renewal")}</th>
                    <th className="text-right px-5 py-4 font-bold">{t("slab_extra")}</th>
                    <th className="text-right px-5 py-4 font-bold">{t("slab_total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {DSA_SLABS.map((s, i) => (
                    <tr key={i} className="border-t border-white/10 hover:bg-white/5 transition-colors duration-200">
                      <td className="px-5 py-4 tabular text-white/90">
                        {s.above ? `${t("slab_above")} ₹${s.label.replace("+", "")}` : s.upto ? `${t("slab_upto")} ₹${s.label}` : `₹${s.label}`}
                      </td>
                      <td className="px-5 py-4 text-right tabular">{s.payout}%</td>
                      <td className="px-5 py-4 text-right tabular">{s.renewal}%</td>
                      <td className="px-5 py-4 text-right tabular">{s.extra}%</td>
                      <td className="px-5 py-4 text-right tabular font-bold text-[#D4AF37]">{s.payout + s.renewal + s.extra}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.2}>
            <div className="grid grid-cols-2 gap-4">
              {features.map((f, i) => (
                <div key={i} className="border border-white/15 p-6 hover:border-[#D4AF37] hover:-translate-y-1 transition-all duration-300">
                  <f.icon size={26} strokeWidth={1.5} className="text-[#D4AF37]" />
                  <div className="mt-4 text-sm font-bold">{f.label}</div>
                </div>
              ))}
            </div>
            <Link
              to="/register?role=dsa"
              data-testid="dsa-join-button"
              className="mt-8 flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] py-4 text-sm font-bold tracking-wide hover:bg-white transition-colors duration-300"
            >
              {t("dsa_join")} <ArrowRight size={16} />
            </Link>
            <Link
              to="/dsa-login"
              data-testid="dsa-login-link"
              className="mt-3 flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] py-4 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300"
            >
              {t("login_dsa_title")}
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Security = () => {
  const { t } = useLang();
  const items = [
    { icon: FileText, title: t("sec1_t"), desc: t("sec1_d") },
    { icon: ShieldCheck, title: t("sec2_t"), desc: t("sec2_d") },
    { icon: MonitorSmartphone, title: t("sec3_t"), desc: t("sec3_d") },
    { icon: Banknote, title: t("sec4_t"), desc: t("sec4_d") },
  ];
  return (
    <section className="py-24 lg:py-32" data-testid="security-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <Eyebrow>{t("sec_eyebrow")}</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A]">{t("sec_title")}</h2>
            <p className="text-base md:text-lg text-[#5C5C5C] mt-5 leading-relaxed">{t("sec_sub")}</p>
            <div className="relative mt-10">
              <div className="absolute -inset-3 border border-[#D4AF37] -translate-x-4 -translate-y-4 pointer-events-none" />
              <img src={IMAGES.family} alt="Family secured by Matrix Works" className="w-full h-72 object-cover" data-testid="trust-image" />
            </div>
            <blockquote className="mt-10 border-l-2 border-[#D4AF37] pl-6">
              <p className="font-display italic text-lg text-[#1A1A1A] leading-relaxed">“{t("trust_quote")}”</p>
              <footer className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("trust_name")}</footer>
            </blockquote>
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {items.map((it, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-white border border-[#E5E2D9] p-8 h-full hover:-translate-y-1 hover:shadow-lg hover:border-[#D4AF37] transition-all duration-300" data-testid={`security-card-${i}`}>
                  <it.icon size={30} strokeWidth={1.5} className="text-[#D4AF37]" />
                  <h3 className="font-display text-xl font-bold text-[#1A1A1A] mt-5">{it.title}</h3>
                  <p className="text-sm text-[#5C5C5C] leading-relaxed mt-2">{it.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const { t } = useLang();
  const [payment, setPayment] = useState(null);
  useEffect(() => {
    api.get("/settings/payment").then((r) => setPayment(r.data)).catch(() => {});
  }, []);
  const qrSrc = payment?.qr_path ? `${API}/settings/qr` : QR_PAYMENT.url;
  const upiId = payment?.upi_id || QR_PAYMENT.upi;
  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#F3F1EB]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <Eyebrow>{t("contact_eyebrow")}</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] max-w-xl">{t("contact_title")}</h2>
          <p className="text-base md:text-lg text-[#5C5C5C] mt-5 max-w-lg leading-relaxed">{t("contact_sub")}</p>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.15}>
          <div className="bg-white border border-[#E5E2D9] divide-y divide-[#E5E2D9]">
            <a href="tel:+919825708561" className="flex items-center gap-5 p-7 hover:bg-[#FDFCF8] transition-colors duration-200 group" data-testid="contact-phone-link">
              <span className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-200">
                <Phone size={20} className="text-[#D4AF37] group-hover:text-[#1A1A1A]" />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("contact_call")}</div>
                <div className="font-display text-xl font-bold text-[#1A1A1A] tabular mt-1">+91 98257 08561</div>
              </div>
            </a>
            <a href={waLink("Hello Matrix Works, I want to know more about investment plans.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-5 p-7 hover:bg-[#FDFCF8] transition-colors duration-200 group" data-testid="contact-whatsapp-link">
              <span className="w-12 h-12 bg-[#25D366] flex items-center justify-center text-white group-hover:bg-[#1da851] transition-colors duration-200">
                <WhatsAppIcon size={20} />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("wa_chat")}</div>
                <div className="font-display text-xl font-bold text-[#1A1A1A] tabular mt-1">+91 98257 08561</div>
              </div>
            </a>
            <div className="flex items-center gap-5 p-7" data-testid="contact-address">
              <span className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center">
                <MapPin size={20} className="text-[#D4AF37]" />
              </span>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("contact_office")}</div>
                <div className="text-sm font-semibold text-[#1A1A1A] mt-1 leading-relaxed">
                  14th Floor, Mandal Heights, S.G. Highway, Ahmedabad, Gujarat
                </div>
              </div>
            </div>
            <div className="flex items-center gap-5 p-7" data-testid="contact-qr">
              <img src={qrSrc} alt="Scan and Pay via UPI" className="w-20 h-20 object-contain border border-[#E5E2D9]" />
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("qr_title")}</div>
                <div className="text-sm font-semibold text-[#1A1A1A] mt-1 tabular">{t("qr_upi")}: {upiId}</div>
                {payment?.account_number && (
                  <div className="text-xs text-[#5C5C5C] mt-1 tabular">A/C: {payment.account_number} · IFSC: {payment.ifsc}</div>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-[#1A1A1A] text-white py-14 noise-overlay" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
        <Logo size={40} dark />
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold text-center">{t("footer_tag")}</p>
        <p className="text-xs text-white/50">{t("footer_rights")}</p>
      </div>
    </footer>
  );
};

export default function Landing() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navbar />
      <Hero />
      <OffersTicker />
      <Stats />
      <Marquee />
      <Plans />
      <LoanPlanSection />
      <CalcSection />
      <DsaSection />
      <Security />
      <Contact />
      <Footer />
    </div>
  );
}
